import { getProductImage } from './productImage';
import { getDisplayPrice } from './productPricing';

const STORAGE_PREFIX = 'sifrifana:user-dashboard';

const cloneValue = (value) => JSON.parse(JSON.stringify(value));

const canUseStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const buildStorageKey = (scope, userId) => `${STORAGE_PREFIX}:${scope}:${userId || 'guest'}`;

const LEGACY_SEEDED_IDS = {
  orders: new Set([
    'dashboard-order-1',
    'dashboard-order-2',
    'dashboard-order-3',
    'dashboard-order-4',
  ]),
  'payment-methods': new Set(['payment-method-1', 'payment-method-2']),
  'delivery-addresses': new Set(['delivery-address-1', 'delivery-address-2']),
};

const removeLegacySeedItems = (scope, value) => {
  if (!Array.isArray(value) || !LEGACY_SEEDED_IDS[scope]) {
    return value;
  }

  return value.filter((item) => !LEGACY_SEEDED_IDS[scope].has(item?.id));
};

export const readUserDashboardState = (scope, userId, fallback) => {
  if (!canUseStorage() || !userId) {
    return cloneValue(fallback);
  }

  try {
    const storageKey = buildStorageKey(scope, userId);
    const raw = window.localStorage.getItem(storageKey);

    if (!raw) {
      return cloneValue(fallback);
    }

    const parsedValue = JSON.parse(raw);
    const cleanedValue = removeLegacySeedItems(scope, parsedValue);

    if (cleanedValue !== parsedValue) {
      window.localStorage.setItem(storageKey, JSON.stringify(cleanedValue));
    }

    return cleanedValue;
  } catch (error) {
    console.warn(`Failed to read "${scope}" dashboard state:`, error);
    return cloneValue(fallback);
  }
};

export const writeUserDashboardState = (scope, userId, value) => {
  if (!canUseStorage() || !userId) {
    return;
  }

  try {
    window.localStorage.setItem(buildStorageKey(scope, userId), JSON.stringify(value));
  } catch (error) {
    console.warn(`Failed to persist "${scope}" dashboard state:`, error);
  }
};

export const createDefaultOrders = () => ([
]);

export const createDefaultPaymentMethods = () => ([
]);

export const createDefaultAddresses = () => ([
]);

export const createDashboardOrderFromCartItems = (items) => {
  const cartItems = Array.isArray(items) ? items : [];
  const firstItem = cartItems[0] || {};
  const firstProduct = firstItem.product || {};
  const productImage = getProductImage(firstProduct);
  const totalQuantity = cartItems.reduce((sum, item) => sum + Number(item.quantity || item.qty || 1), 0);
  const total = cartItems.reduce((sum, item) => {
    const quantity = Number(item.quantity || item.qty || 1);
    const price = getDisplayPrice(item.product || item);

    return sum + price * quantity;
  }, 0);

  return {
    id: `order-${Date.now()}`,
    productId: firstProduct.id || firstProduct._id || firstItem.productId || '',
    title: firstProduct.title || firstProduct.name || 'Order',
    specialCode: firstProduct.specialCode || firstItem.specialCode || firstProduct.productCode || firstItem.productCode || firstProduct.code || firstItem.code || '',
    brand: firstProduct.brand || '',
    category: firstProduct.category || '',
    image: productImage,
    images: productImage ? [productImage] : firstProduct.images || [],
    extraItems: Math.max(totalQuantity - 1, 0),
    total,
    status: 'ordered',
    createdAt: new Date().toISOString(),
  };
};

export const ORDER_STATUS_META = {
  ordered: {
    label: 'Ordered',
    labelKey: 'orders.status.ordered',
    accent: '#b629df',
  },
  processing: {
    label: 'Processing',
    labelKey: 'orders.status.processing',
    accent: '#b629df',
  },
  shipped: {
    label: 'Shipped',
    labelKey: 'orders.status.shipped',
    accent: '#b629df',
  },
  delivered: {
    label: 'Delivered',
    labelKey: 'orders.status.delivered',
    accent: '#b629df',
  },
  cancelled: {
    label: 'Cancelled',
    labelKey: 'orders.status.cancelled',
    accent: '#e34949',
  },
};
